import { useMemo, useState } from "react";
import {
  Activity,
  CheckCircle2,
  Edit3,
  Plus,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import { EmptyState, Field, StatusPill } from "../components/Ui";
import { blankCriticalControl } from "../lib/projectStore";
import type {
  ControlHealth,
  ControlHierarchy,
  CriticalControl,
  DesignRisk,
  EvidenceItem,
} from "../types/domain";

interface ControlsProps {
  controls: CriticalControl[];
  risks: DesignRisk[];
  evidence: EvidenceItem[];
  onSave: (control: CriticalControl) => void;
  onRemove: (id: string) => void;
}

const tone = (health: ControlHealth) =>
  health === "effective"
    ? "success"
    : health === "degraded" || health === "not-verified"
      ? "warning"
      : "danger";

export function ControlsView({
  controls,
  risks,
  evidence,
  onSave,
  onRemove,
}: ControlsProps) {
  const [draft, setDraft] = useState<CriticalControl | null>(null);
  const [scenario, setScenario] = useState("all");
  const scenarios = useMemo(
    () => [...new Set(controls.map((item) => item.majorAccidentScenario).filter(Boolean))],
    [controls],
  );
  const visible = controls.filter(
    (item) => scenario === "all" || item.majorAccidentScenario === scenario,
  );

  const save = () => {
    if (!draft) return;
    onSave({ ...draft, updatedAt: new Date().toISOString() });
    setDraft(null);
  };

  return (
    <>
      <section className="module-intro">
        <div>
          <span className="feature-kicker">
            <ShieldAlert size={16} /> Critical control assurance
          </span>
          <h2>Verify the few controls that must not fail.</h2>
          <p>
            Build a bowtie-style view around major accident scenarios. Every preventive and
            mitigative control gets a performance standard, named owner, evidence and verification
            frequency.
          </p>
        </div>
        <a
          className="button button-secondary"
          href="https://www.energyinst.org/technical/publications/topics/human-and-organisational-factors/bow-ties-in-risk-management-a-concept-book-for-process-safety"
          target="_blank"
          rel="noreferrer"
        >
          Bowtie framework
        </a>
      </section>

      <section className="toolbar">
        <select className="toolbar-select" value={scenario} onChange={(e) => setScenario(e.target.value)}>
          <option value="all">All major accident scenarios</option>
          {scenarios.map((value) => <option key={value}>{value}</option>)}
        </select>
        <button className="button button-primary" onClick={() => setDraft(blankCriticalControl())}>
          <Plus size={18} /> Add critical control
        </button>
      </section>

      {controls.length === 0 ? (
        <EmptyState
          icon={<Activity size={32} />}
          title="No critical controls defined"
          action={
            <button className="button button-primary" onClick={() => setDraft(blankCriticalControl())}>
              <Plus size={18} /> Define the first control
            </button>
          }
        >
          Start from a fatal or major-accident scenario, identify the controls that prevent or
          mitigate it, then define how each control will be verified.
        </EmptyState>
      ) : (
        <section className="bowtie-list">
          {visible.map((control) => (
            <article className={`control-card control-${control.health}`} key={control.id}>
              <header>
                <div>
                  <span className="eyebrow">{control.controlType} · {control.hierarchy}</span>
                  <h3>{control.title || "Untitled control"}</h3>
                </div>
                <StatusPill status={tone(control.health)}>{control.health}</StatusPill>
              </header>
              <div className="scenario-strip">
                <ShieldAlert size={18} />
                <span>
                  <small>Major accident scenario</small>
                  <strong>{control.majorAccidentScenario || "Not defined"}</strong>
                </span>
              </div>
              <div className="control-grid">
                <div>
                  <small>Performance standard</small>
                  <p>{control.performanceStandard || "Not defined"}</p>
                </div>
                <div>
                  <small>Verification method</small>
                  <p>{control.verificationMethod || "Not defined"}</p>
                </div>
                <div>
                  <small>Accountability</small>
                  <p>{control.owner || "Unassigned"} · {control.verificationFrequency}</p>
                </div>
                <div>
                  <small>Next verification</small>
                  <p>{control.nextDueDate || "Not scheduled"}</p>
                </div>
              </div>
              <footer>
                <span>{control.linkedRiskIds.length} linked risk(s)</span>
                <span>{control.evidenceIds.length} evidence record(s)</span>
                <div className="row-actions">
                  <button className="icon-button" aria-label="Edit control" onClick={() => setDraft(control)}>
                    <Edit3 size={16} />
                  </button>
                  <button
                    className="icon-button danger"
                    aria-label="Delete control"
                    onClick={() => window.confirm("Delete this critical control?") && onRemove(control.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </footer>
            </article>
          ))}
        </section>
      )}

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">Bowtie control record</span>
                <h2>{controls.some((item) => item.id === draft.id) ? "Edit" : "Add"} critical control</h2>
              </div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close">
                <X size={21} />
              </button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <Field label="Control title" wide>
                  <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                </Field>
                <Field label="Major accident scenario" wide>
                  <textarea rows={3} value={draft.majorAccidentScenario} onChange={(e) => setDraft({ ...draft, majorAccidentScenario: e.target.value })} />
                </Field>
                <Field label="Bowtie side">
                  <select value={draft.controlType} onChange={(e) => setDraft({ ...draft, controlType: e.target.value as CriticalControl["controlType"] })}>
                    <option value="preventive">Preventive</option>
                    <option value="mitigative">Mitigative</option>
                  </select>
                </Field>
                <Field label="Hierarchy">
                  <select value={draft.hierarchy} onChange={(e) => setDraft({ ...draft, hierarchy: e.target.value as ControlHierarchy })}>
                    <option value="elimination">Elimination</option>
                    <option value="substitution">Substitution</option>
                    <option value="engineering">Engineering</option>
                    <option value="administrative">Administrative</option>
                    <option value="ppe">PPE</option>
                  </select>
                </Field>
                <Field label="Performance standard" wide>
                  <textarea rows={3} value={draft.performanceStandard} onChange={(e) => setDraft({ ...draft, performanceStandard: e.target.value })} placeholder="What must be true for this control to be effective?" />
                </Field>
                <Field label="Verification method" wide>
                  <textarea rows={3} value={draft.verificationMethod} onChange={(e) => setDraft({ ...draft, verificationMethod: e.target.value })} placeholder="Inspection, test, calculation, observation or audit" />
                </Field>
                <Field label="Control owner">
                  <input value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })} />
                </Field>
                <Field label="Verification frequency">
                  <input value={draft.verificationFrequency} onChange={(e) => setDraft({ ...draft, verificationFrequency: e.target.value })} />
                </Field>
                <Field label="Control health">
                  <select value={draft.health} onChange={(e) => setDraft({ ...draft, health: e.target.value as ControlHealth })}>
                    <option value="not-verified">Not verified</option>
                    <option value="effective">Effective</option>
                    <option value="degraded">Degraded</option>
                    <option value="failed">Failed</option>
                  </select>
                </Field>
                <Field label="Last verified">
                  <input type="date" value={draft.lastVerifiedDate} onChange={(e) => setDraft({ ...draft, lastVerifiedDate: e.target.value })} />
                </Field>
                <Field label="Next due">
                  <input type="date" value={draft.nextDueDate} onChange={(e) => setDraft({ ...draft, nextDueDate: e.target.value })} />
                </Field>
                <Field label="Linked design risks" wide>
                  <div className="check-grid">
                    {risks.map((risk) => (
                      <label key={risk.id}>
                        <input
                          type="checkbox"
                          checked={draft.linkedRiskIds.includes(risk.id)}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              linkedRiskIds: e.target.checked
                                ? [...draft.linkedRiskIds, risk.id]
                                : draft.linkedRiskIds.filter((id) => id !== risk.id),
                            })
                          }
                        />
                        <span>{risk.designElement || risk.hazard || "Untitled risk"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Verification evidence" wide>
                  <div className="check-grid">
                    {evidence.map((item) => (
                      <label key={item.id}>
                        <input
                          type="checkbox"
                          checked={draft.evidenceIds.includes(item.id)}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              evidenceIds: e.target.checked
                                ? [...draft.evidenceIds, item.id]
                                : draft.evidenceIds.filter((id) => id !== item.id),
                            })
                          }
                        />
                        <span>{item.title || "Untitled evidence"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Notes and degradation factors" wide>
                  <textarea rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              <div className="quality-summary">
                {draft.title && draft.majorAccidentScenario && draft.owner ? (
                  <><CheckCircle2 size={17} /> Core control standard complete</>
                ) : (
                  <><ShieldAlert size={17} /> Title, scenario and owner are required</>
                )}
              </div>
              <button className="button button-secondary" onClick={() => setDraft(null)}>Cancel</button>
              <button className="button button-primary" disabled={!draft.title.trim() || !draft.majorAccidentScenario.trim() || !draft.owner.trim()} onClick={save}>
                Save critical control
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
